import { Map as ImmutableMap, OrderedMap, Record } from 'immutable';
import { createSelector } from 'reselect';

import { chartOfAccountsUtils } from '../chart-of-accounts';
import { memorizedRulesSelectors } from '../memorized-rules';
import { transactionsSelectors } from '../transactions';

import { REDUCER_KEY } from './renameRulesReducer';
import { containsPayeeQCS, normalizePayeeQCS } from './renameRulesUtils';

export const getStore = (state) => state[REDUCER_KEY];
export const getLoadPending = (state) => getStore(state).loadPending;
export const getIsLoading = (state) => getStore(state).isLoading;
export const getError = (state) => getStore(state).error;
export const getResourcesById = (state) => getStore(state).resourcesById;
export const getLastSyncDate = (state) => getStore(state).lastSyncDate;

export const getRulesById = createSelector(getResourcesById, (resourcesById) =>
  resourcesById.sort((a, b) => a?.renamePayeeTo?.localeCompare(b.renamePayeeTo)),
);

export const getPayeeApplicableTransactions = createSelector(
  (state) => transactionsSelectors.getTransactionsOfTypes(state, transactionsSelectors.TxnTypes.REGULAR_TXNS),
  (state, payeeFrom) => payeeFrom,
  (state, payeeFrom, payeeTo) => payeeTo,
  (transactions, payeeFrom, payeeTo) => {
    const payeeKeyWords = normalizePayeeQCS(payeeFrom);
    const normalizedPayeeTo = normalizePayeeQCS(payeeTo);
    return transactions.filter((transaction) => {
      const statementPayee = normalizePayeeQCS(transaction?.cpData?.payee);
      const inferredPayee = normalizePayeeQCS(transaction?.cpData?.inferredPayee);
      const userPayee = normalizePayeeQCS(transaction.payee);
      return (
        (containsPayeeQCS(statementPayee, payeeKeyWords) || containsPayeeQCS(inferredPayee, payeeKeyWords)) &&
        userPayee !== normalizedPayeeTo
      );
    });
  },
);

const RuleNode = Record({
  payee: undefined,
  coaString: undefined,
  memorizedRule: undefined,
  renameRules: undefined,
});

export const getRuleNodes = createSelector(
  memorizedRulesSelectors.getMemorizedRulesById,
  getRulesById,
  memorizedRulesSelectors.getMemorizedRuleCoaStrings,
  (memorizedRules, renameRules, coaStrings) =>
    OrderedMap()
      .withMutations((ruleNodes) => {
        memorizedRules.forEach((memorizedRule) => {
          const coaString = coaStrings.get(chartOfAccountsUtils.getCoaId(memorizedRule.coa));
          ruleNodes.set(
            memorizedRule.payee.trim().toLowerCase(),
            RuleNode({
              payee: memorizedRule.payee,
              coaString,
              memorizedRule,
            }),
          );
        });

        renameRules.forEach((renameRule) => {
          let ruleNode = ruleNodes.get(renameRule.renamePayeeTo.trim().toLowerCase());
          const newRenameRules = ImmutableMap({ [renameRule.id]: renameRule });
          if (ruleNode) {
            ruleNode = ruleNode.mergeDeep({ renameRules: newRenameRules });
          } else {
            ruleNode = RuleNode({
              payee: renameRule.renamePayeeTo,
              renameRules: newRenameRules,
            });
          }
          ruleNodes.set(ruleNode.payee.trim().toLowerCase(), ruleNode);
        });
      })
      .sortBy((ruleNode) => ruleNode.payee),
);
